import FullCalendar from '@fullcalendar/react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MenuItem, SelectChangeEvent, Stack } from '@mui/material';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { RefObject, useEffect, useState } from 'react';
import { DEFAULT_POSITION_ALL, layoutTypeToPositionsKey } from 'src/constants';
import { IDaypart, ProjectConfigInterface } from 'src/interfaces';
import { lang } from 'src/utils';

import {
  CalendarToolbarDirection,
  ECalendarViewsId,
  EHeaderToolbarDirections,
} from '../Calendar.interface';
import {
  BorderedStack,
  RoundedDropdown,
  StyledIconButton,
  StyledStack,
  StyledTodayButton,
  StyledTypography,
} from './CalendarHeader.styled';

interface CalendarHeaderProps {
  calendarRef: RefObject<FullCalendar>;
  position?: string;
  viewType?: string;
  layoutType?: string;
  selectedDaypartId?: string;
  daypartOptions: IDaypart[];
  isLoadingDayparts: boolean;
  shouldDisableDropdown?: boolean;
  onDaypartChange: (value: string) => void;
  onViewChange: (viewType: string) => void;
  onPositionChange: (value: string) => void;
  projectConfig?: Partial<ProjectConfigInterface>;
}

dayjs.extend(advancedFormat);

export const CalendarHeader = ({
  calendarRef,
  onDaypartChange,
  onViewChange,
  onPositionChange,
  daypartOptions,
  isLoadingDayparts,
  selectedDaypartId,
  viewType,
  position,
  layoutType,
  shouldDisableDropdown,
  projectConfig,
}: CalendarHeaderProps) => {
  const [dateRange, setDateRange] = useState<string>('');

  const key = layoutType ? layoutTypeToPositionsKey[layoutType] : undefined;
  const totalPositions = key ? projectConfig?.[key] : undefined;

  const formatCalendarRange = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const view = calendarApi.view;
    const start = dayjs(view.currentStart);
    const end = dayjs(view.currentEnd).subtract(1, 'day');

    let range = '';

    switch (view.type) {
      case ECalendarViewsId.resourceTimelineWeek: {
        const sameMonth = start.month() === end.month();
        const sameYear = start.year() === end.year();

        if (sameMonth && sameYear) {
          range = `${start.format('D')} - ${end.format('Do')} ${end.format(
            'MMM YYYY',
          )}`;
        } else {
          range = `${start.format('D MMM')} - ${end.format('Do MMM YYYY')}`;
        }
        break;
      }
      case ECalendarViewsId.resourceTimelineMonth: {
        range = start.format('MMMM YYYY');
        break;
      }
      case ECalendarViewsId.resourceTimelineYear: {
        range = start.format('YYYY');
        break;
      }
      default: {
        range = `${start.format('D MMM YYYY')} - ${end.format('D MMM YYYY')}`;
        break;
      }
    }

    setDateRange(range);
  };

  const handleNavigation = (direction: CalendarToolbarDirection) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const actions = {
      prev: () => calendarApi.prev(),
      next: () => calendarApi.next(),
      today: () => calendarApi.today(),
    };
    actions[direction]?.();

    formatCalendarRange();
  };

  const handleViewChange = (event: SelectChangeEvent<string>) => {
    const newView = event.target.value;

    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi || calendarApi.view.type === newView) return;

    onViewChange(newView);
    calendarApi.changeView(newView);

    formatCalendarRange();
  };

  useEffect(() => {
    // Set the initial view of the calendar
    formatCalendarRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}>
      <Stack direction="row">
        <BorderedStack direction="row" spacing={1}>
          <StyledIconButton
            color="primary"
            data-testid="prev-button"
            onClick={() => handleNavigation(EHeaderToolbarDirections.prev)}>
            <ChevronLeftIcon />
          </StyledIconButton>

          <StyledTypography variant="body1">
            {dateRange || dayjs().format('MMMM YYYY')}
          </StyledTypography>

          <StyledIconButton
            color="primary"
            data-testid="next-button"
            onClick={() => handleNavigation(EHeaderToolbarDirections.next)}>
            <ChevronRightIcon />
          </StyledIconButton>
        </BorderedStack>

        <StyledTodayButton
          onClick={() => handleNavigation(EHeaderToolbarDirections.today)}
          variant="text"
          size="small">
          {lang.today}
        </StyledTodayButton>
      </Stack>

      <StyledStack direction="row">
        <RoundedDropdown
          id="viewSelector"
          onChange={handleViewChange}
          value={viewType}
          disabled={shouldDisableDropdown}
          MenuProps={{
            PaperProps: {
              sx: {
                mt: 1,
              },
            },
          }}>
          <MenuItem value={ECalendarViewsId.resourceTimelineYear}>
            {lang.yearly}
          </MenuItem>
          <MenuItem value={ECalendarViewsId.resourceTimelineMonth}>
            {lang.monthly}
          </MenuItem>
          <MenuItem value={ECalendarViewsId.resourceTimelineWeek}>
            {lang.weekly}
          </MenuItem>
        </RoundedDropdown>

        {totalPositions && (
          <RoundedDropdown
            id="positionDropdown"
            value={position}
            disabled={shouldDisableDropdown}
            onChange={(e: SelectChangeEvent<string>) =>
              onPositionChange(e.target.value)
            }
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 1,
                },
              },
            }}>
            {[...Array(totalPositions)].map((_, index) => {
              const value = index + 1;

              return (
                <MenuItem key={value} value={value}>
                  {`Position ${value}`}
                </MenuItem>
              );
            })}
            <MenuItem value={DEFAULT_POSITION_ALL}>
              {lang.scheduleComponent.positionDropdownAll}
            </MenuItem>
          </RoundedDropdown>
        )}

        <RoundedDropdown
          id="daypartSelector"
          value={daypartOptions?.length ? selectedDaypartId : ''}
          disabled={shouldDisableDropdown}
          onChange={(e: SelectChangeEvent<string>) =>
            onDaypartChange(e.target.value)
          }
          MenuProps={{
            PaperProps: {
              sx: {
                mt: 1,
              },
            },
          }}>
          {!isLoadingDayparts &&
            [...daypartOptions]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((daypart) => (
                <MenuItem key={daypart.id} value={daypart.id}>
                  {daypart.name}
                </MenuItem>
              ))}
        </RoundedDropdown>
      </StyledStack>
    </Stack>
  );
};
